import { rangesOverlapHalfOpen } from '@/utils/calendarBlockOverlap';

export type BlockOverlapKind = 'appointment' | 'block';

export interface BlockTargetOccupant {
  id: string;
  clinicianId: string | null;
  isBlock: boolean;
  label: string;
  startsAt: Date;
  endsAt: Date;
}

export interface PractitionerBlockConflict {
  clinicianId: string;
  clinicianName: string;
  kind: BlockOverlapKind;
  detail: string;
}

export type MultiBlockSaveChoice = 'all' | 'free';

/**
 * One practitioner still gets the hard refusal from CLISUP-164.
 * Hele klinikken and any selection of several practitioners get a warning
 * and can still be saved.
 */
export function blockOverlapSaveMode(clinicianCount: number): 'refuse' | 'choose' {
  return clinicianCount > 1 ? 'choose' : 'refuse';
}

function clinicianName(id: string, names: ReadonlyMap<string, string>): string {
  const name = names.get(id)?.trim();
  return name || 'Behandler';
}

/**
 * Practitioners who already have an appointment or a block in the range.
 * Working hours are not occupants, so a closed or short day is not a conflict.
 */
export function multiBlockConflicts(input: {
  clinicianIds: readonly string[];
  names: ReadonlyMap<string, string>;
  start: Date;
  end: Date;
  occupants: readonly BlockTargetOccupant[];
  formatClock: (date: Date) => string;
}): PractitionerBlockConflict[] {
  if (!(input.end > input.start)) return [];
  const conflicts: PractitionerBlockConflict[] = [];
  for (const clinicianId of input.clinicianIds) {
    const hits = input.occupants
      .filter(
        (row) =>
          row.clinicianId === clinicianId &&
          rangesOverlapHalfOpen(input.start, input.end, row.startsAt, row.endsAt),
      )
      .sort((a, b) => {
        if (a.isBlock !== b.isBlock) return a.isBlock ? 1 : -1;
        return a.startsAt.getTime() - b.startsAt.getTime();
      });
    for (const hit of hits) {
      const label = hit.label.trim() || (hit.isBlock ? 'Blokering' : 'Aftale');
      conflicts.push({
        clinicianId,
        clinicianName: clinicianName(clinicianId, input.names),
        kind: hit.isBlock ? 'block' : 'appointment',
        detail: `${label} ${input.formatClock(hit.startsAt)}–${input.formatClock(hit.endsAt)}`,
      });
    }
  }
  return conflicts;
}

export function clinicianIdsForBlockSave(
  clinicianIds: readonly string[],
  conflicts: readonly PractitionerBlockConflict[],
  choice: MultiBlockSaveChoice,
): string[] {
  if (choice === 'all') return [...clinicianIds];
  const busy = new Set(conflicts.map((row) => row.clinicianId));
  return clinicianIds.filter((id) => !busy.has(id));
}

export function groupBlockConflicts(
  conflicts: readonly PractitionerBlockConflict[],
): { clinicianId: string; clinicianName: string; items: PractitionerBlockConflict[] }[] {
  const groups: { clinicianId: string; clinicianName: string; items: PractitionerBlockConflict[] }[] =
    [];
  for (const row of conflicts) {
    const current = groups.find((group) => group.clinicianId === row.clinicianId);
    if (current) current.items.push(row);
    else groups.push({ clinicianId: row.clinicianId, clinicianName: row.clinicianName, items: [row] });
  }
  return groups;
}
